// =============================================================================
// client/src/lib/flux/useMe.js | v1.0 | S79 | P9a — W-7(f)
// =============================================================================
// GET /api/auth/me once, shaped for the flux surfaces.
//
// The house pattern already loads /api/auth/me on every P9 page
// (RecruitList.jsx, PlanProgress.jsx, RecruitEdit.jsx, Huddler.jsx). This is
// that pattern as a hook, so the campaign pages do not each grow their own copy.
//
// The server returns { auth:{id,email}, team:{...}, permissions:{key:bool} }.
// `team` is the client's ONLY role source and — under ruling 35 — also the
// source of the test-send recipient. The client NEVER enforces a role; it hides
// controls the server would refuse and renders the server's 403 verbatim when
// one arrives (§1.1).
//
// S83: `permissions` is the server's RESOLVED capability map (role defaults with
// this member's overrides applied). The bound `can` below reads it. During the
// deploy window it may be absent — Vercel and Railway ship independently — and
// can.js falls back to the S80 role table rather than crashing or blanking the
// UI. See client/src/lib/can.js for why that fallback exists.
// =============================================================================

import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { can as rawCan } from '../can.js';

export function useMe() {
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/auth/me')
      .then((data) => { if (!cancelled) setMe(data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const role = me?.team?.role || null;

  return {
    me,
    loading,
    error,
    role,
    // The server's resolved answer. `{}` rather than null so a caller can read
    // it without a guard; can() distinguishes absent-from-response by checking
    // `me.permissions` itself, not this default.
    permissions: me?.permissions || {},
    // Bound to `me`, so every call site asks the same object the same way.
    // Safe before the fetch resolves: can(null, k) is false.
    can: (action) => rawCan(me, action),
    // Ruling 35: the test-send recipient is BOUND to this address. There is no
    // recipient input anywhere in P9a — the operator can see where a test goes
    // and cannot change it.
    teamEmail: me?.team?.email || null,
    teamName: me?.team?.full_name || null,
    teamFirstName: me?.team?.first_name || null,
    teamLastName: me?.team?.last_name || null,
    isAdminOrManager: role === 'admin' || role === 'manager',
    isRecruitingCoordinator: role === 'recruiting_coordinator',
  };
}

export default useMe;
